import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fmtDate } from "../lib/format";
import {
  Plus,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Settings2,
  X,
  Receipt,
} from "lucide-react";
import { toast } from "sonner";

const SOURCES = [
  { v: "chime", l: "Chime", currency: "USD" },
  { v: "cashapp", l: "CashApp", currency: "USD" },
  { v: "paypal", l: "PayPal", currency: "USD" },
  { v: "venmo", l: "Venmo", currency: "USD" },
  { v: "usdt", l: "USDT", currency: "USDT" },
  { v: "other", l: "Other", currency: "USD" },
];
const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.v, s.l]));
const SOURCE_TONE = {
  chime: "bg-emerald-50 border-emerald-200 text-emerald-700",
  cashapp: "bg-sky-50 border-sky-200 text-sky-700",
  paypal: "bg-indigo-50 border-indigo-200 text-indigo-700",
  venmo: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700",
  usdt: "bg-amber-50 border-amber-200 text-amber-800",
  other: "bg-zinc-100 border-zinc-200 text-zinc-700",
};

const fmtUSDT = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });

const monthAdd = (ym, n) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const currentMonth = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
  date: todayISO(),
  source: "chime",
  amount_usd: "",
  rate_usd_to_usdt: "",
  amount_usdt: "",
  wallet_id: "",
  category: "",
  description: "",
  notes: "",
};

export default function Expenses() {
  const [month, setMonth] = useState(currentMonth());
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [catOpen, setCatOpen] = useState(false);
  const [catDraft, setCatDraft] = useState([]);
  const [catInput, setCatInput] = useState("");

  const monthRange = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const start = `${month}-01`;
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const end = `${month}-${String(last).padStart(2, "0")}`;
    return { from: start, to: end };
  }, [month]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from: monthRange.from, to: monthRange.to };
      if (sourceFilter !== "all") params.source = sourceFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      const [list, sum, cats, w] = await Promise.all([
        api.get("/expenses", { params }),
        api.get("/expenses/summary", { params: { month } }),
        api.get("/expenses/categories"),
        api.get("/wallets"),
      ]);
      setItems(list.data);
      setSummary(sum.data);
      setCategories(cats.data.categories || []);
      setWallets(w.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [month, sourceFilter, categoryFilter]);

  const walletsForSource = useMemo(() => {
    if (form.source === "usdt") return wallets.filter((w) => w.type === "link");
    if (form.source === "other") return [];
    return wallets.filter((w) => w.type === form.source);
  }, [wallets, form.source]);

  const isUsdtSource = form.source === "usdt";
  const previewUsdt = useMemo(() => {
    if (isUsdtSource) return Number(form.amount_usdt) || 0;
    const usd = Number(form.amount_usd);
    const rate = Number(form.rate_usd_to_usdt);
    if (!usd || !rate) return 0;
    return usd * rate;
  }, [isUsdtSource, form.amount_usd, form.rate_usd_to_usdt, form.amount_usdt]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      date: monthRange.to >= todayISO() ? todayISO() : monthRange.to,
    });
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      date: e.date,
      source: e.source,
      amount_usd: e.amount_usd != null ? String(e.amount_usd) : "",
      rate_usd_to_usdt:
        e.rate_usd_to_usdt != null ? String(e.rate_usd_to_usdt) : "",
      amount_usdt: e.source === "usdt" ? String(e.amount_usdt || "") : "",
      wallet_id: e.wallet_id || "",
      category: e.category || "",
      description: e.description || "",
      notes: e.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.description?.trim())
      return toast.error("Description is required");
    const payload = {
      date: form.date,
      source: form.source,
      wallet_id: form.wallet_id || null,
      category: form.category || null,
      description: form.description.trim(),
      notes: form.notes || null,
    };
    if (isUsdtSource) {
      const u = Number(form.amount_usdt);
      if (!u || u <= 0) return toast.error("Enter a valid USDT amount");
      payload.amount_usdt = u;
    } else {
      const usd = Number(form.amount_usd);
      const rate = Number(form.rate_usd_to_usdt);
      if (!usd || usd <= 0) return toast.error("Enter a valid USD amount");
      if (!rate || rate <= 0)
        return toast.error("Enter a USD→USDT rate (USDT per 1 USD)");
      payload.amount_usd = usd;
      payload.rate_usd_to_usdt = rate;
    }
    try {
      if (editing) {
        await api.put(`/expenses/${editing.id}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.description}"?`)) return;
    try {
      await api.delete(`/expenses/${e.id}`);
      toast.success("Deleted");
      load();
    } catch (er) {
      toast.error(apiErrorText(er));
    }
  };

  const openCats = () => {
    setCatDraft([...categories]);
    setCatInput("");
    setCatOpen(true);
  };
  const saveCats = async () => {
    try {
      await api.put("/expenses/categories", { categories: catDraft });
      toast.success("Categories updated");
      setCatOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  return (
    <Layout
      title="Expenses"
      subtitle="All expenses tracked in USDT — set a USD→USDT rate when paying from USD wallets"
      testId="expenses-page"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={openCats}
            className="h-9"
            data-testid="expenses-manage-categories-btn"
          >
            <Settings2 size={14} className="mr-1.5" /> Manage categories
          </Button>
          <Button
            onClick={openCreate}
            className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="add-expense-btn"
          >
            <Plus size={14} className="mr-1.5" /> Add expense
          </Button>
        </div>
      }
    >
      {/* Month nav */}
      <div className="stat-card p-3 mb-5 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setMonth(monthAdd(month, -1))}
          className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center"
          data-testid="expenses-prev-month"
        >
          <ChevronLeft size={14} strokeWidth={1.7} />
        </button>
        <div
          className="font-display text-[15px] font-semibold tracking-tight px-2"
          data-testid="expenses-current-month"
        >
          {monthLabel(month)}
        </div>
        <button
          onClick={() => setMonth(monthAdd(month, 1))}
          className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center"
          data-testid="expenses-next-month"
        >
          <ChevronRight size={14} strokeWidth={1.7} />
        </button>
        {month !== currentMonth() && (
          <button
            onClick={() => setMonth(currentMonth())}
            className="h-8 px-3 rounded-md text-[12.5px] border hairline bg-white hover:bg-zinc-50 ml-1"
            data-testid="expenses-this-month"
          >
            This month
          </button>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger
              className="h-8 w-[160px]"
              data-testid="expenses-filter-source"
            >
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {SOURCES.map((s) => (
                <SelectItem key={s.v} value={s.v}>
                  {s.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger
              className="h-8 w-[180px]"
              data-testid="expenses-filter-category"
            >
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards (USDT-only) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <div className="stat-card p-4" data-testid="expenses-total-usdt">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Total · USDT
          </div>
          <div className="font-mono text-[22px] font-medium mt-1 text-amber-700">
            {fmtUSDT(summary?.total_usdt)}
          </div>
          <div className="text-[10.5px] text-zinc-500 mt-0.5">
            {monthLabel(month)}
          </div>
        </div>
        <div className="stat-card p-4" data-testid="expenses-count">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Entries
          </div>
          <div className="font-mono text-[22px] font-medium mt-1">
            {summary?.count || 0}
          </div>
          <div className="text-[10.5px] text-zinc-500 mt-0.5">this month</div>
        </div>
        <div className="stat-card p-4" data-testid="expenses-top-category">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Top category
          </div>
          {(() => {
            const entries = Object.entries(summary?.by_category || {});
            if (!entries.length)
              return (
                <div className="font-mono text-[15px] font-medium mt-1 text-zinc-400">
                  —
                </div>
              );
            const [name, info] = entries.sort(
              (a, b) => (b[1].amount_usdt || 0) - (a[1].amount_usdt || 0)
            )[0];
            return (
              <>
                <div className="font-display text-[16px] font-semibold mt-1 truncate">
                  {name}
                </div>
                <div className="text-[10.5px] text-zinc-500 mt-0.5 font-mono">
                  {info.count} entries · {fmtUSDT(info.amount_usdt)} USDT
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Source breakdown (USDT) */}
      {summary?.count > 0 && (
        <div className="stat-card p-4 mb-5">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">
            Breakdown by source · USDT
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {SOURCES.map((s) => {
              const info =
                summary?.by_source?.[s.v] || {
                  amount_usdt: 0,
                  amount_usd: 0,
                  count: 0,
                };
              return (
                <div
                  key={s.v}
                  className="rounded-md border hairline p-3"
                  data-testid={`expenses-source-${s.v}`}
                >
                  <div
                    className={`inline-flex text-[10.5px] px-1.5 py-0.5 rounded border ${
                      SOURCE_TONE[s.v]
                    }`}
                  >
                    {s.l}
                  </div>
                  <div className="font-mono text-[15px] font-medium mt-1.5">
                    {fmtUSDT(info.amount_usdt)} USDT
                  </div>
                  <div className="text-[10.5px] text-zinc-500 mt-0.5">
                    {info.count} entries
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="stat-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-3 px-4 font-medium">Date</th>
                <th className="py-3 px-4 font-medium">Source</th>
                <th className="py-3 px-4 font-medium">Wallet</th>
                <th className="py-3 px-4 font-medium">Category</th>
                <th className="py-3 px-4 font-medium">Description</th>
                <th className="py-3 px-4 font-medium text-right">USDT</th>
                <th className="py-3 px-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-zinc-500">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-zinc-500">
                    <Receipt
                      size={20}
                      strokeWidth={1.5}
                      className="mx-auto mb-2 text-zinc-400"
                    />
                    No expenses recorded for {monthLabel(month)}.
                  </td>
                </tr>
              ) : (
                items.map((e) => (
                  <tr
                    key={e.id}
                    className="row-hover"
                    data-testid={`expense-row-${e.id}`}
                  >
                    <td className="py-3 px-4 font-mono text-[12.5px]">
                      {fmtDate(e.date)}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex text-[11px] px-1.5 py-0.5 rounded border ${
                          SOURCE_TONE[e.source]
                        }`}
                      >
                        {SOURCE_LABEL[e.source] || e.source}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[12.5px] text-zinc-600">
                      {e.wallet_name || "—"}
                    </td>
                    <td className="py-3 px-4 text-[12.5px]">
                      {e.category || (
                        <span className="text-zinc-400">Uncategorized</span>
                      )}
                    </td>
                    <td className="py-3 px-4 max-w-[260px]">
                      <div className="truncate font-medium">
                        {e.description}
                      </div>
                      {e.notes && (
                        <div className="text-[11.5px] text-zinc-500 truncate">
                          {e.notes}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 font-mono text-right text-amber-700">
                      {fmtUSDT(e.amount_usdt)} USDT
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(e)}
                          className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                          data-testid={`edit-expense-${e.id}`}
                        >
                          <Pencil size={14} strokeWidth={1.6} />
                        </button>
                        <button
                          onClick={() => remove(e)}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                          data-testid={`delete-expense-${e.id}`}
                        >
                          <Trash2 size={14} strokeWidth={1.6} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit expense" : "Add expense"}
            </DialogTitle>
            <DialogDescription>
              {isUsdtSource
                ? "Enter the USDT amount paid."
                : "Enter the USD amount paid + the USD→USDT rate. We store the expense in USDT."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date *</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="expense-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Source *</Label>
              <Select
                value={form.source}
                onValueChange={(v) =>
                  setForm({ ...form, source: v, wallet_id: "" })
                }
              >
                <SelectTrigger data-testid="expense-source-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => (
                    <SelectItem key={s.v} value={s.v} data-testid={`expense-source-opt-${s.v}`}>
                      {s.l}
                      <span className="ml-2 text-[10.5px] text-zinc-400 font-mono">
                        {s.currency}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isUsdtSource ? (
              <div className="col-span-2 space-y-1.5">
                <Label>Amount (USDT) *</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={form.amount_usdt}
                  onChange={(e) =>
                    setForm({ ...form, amount_usdt: e.target.value })
                  }
                  placeholder="0.00"
                  data-testid="expense-amount-usdt-input"
                />
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label>Amount (USD) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.amount_usd}
                    onChange={(e) =>
                      setForm({ ...form, amount_usd: e.target.value })
                    }
                    placeholder="0.00"
                    data-testid="expense-amount-usd-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>USD → USDT rate * <span className="text-[10.5px] text-zinc-400">(USDT/USD)</span></Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={form.rate_usd_to_usdt}
                    onChange={(e) =>
                      setForm({ ...form, rate_usd_to_usdt: e.target.value })
                    }
                    placeholder="e.g. 1.0000"
                    data-testid="expense-rate-input"
                  />
                </div>
                <div className="col-span-2">
                  <div className="rounded-md border hairline bg-amber-50/40 px-3 py-2 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-widest text-amber-800">
                      ≈ Stored as
                    </span>
                    <span
                      className="font-mono text-[14px] font-medium text-amber-700"
                      data-testid="expense-preview-usdt"
                    >
                      {fmtUSDT(previewUsdt)} USDT
                    </span>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-1.5 col-span-2">
              <Label>Category</Label>
              <Select
                value={form.category || "__none"}
                onValueChange={(v) =>
                  setForm({ ...form, category: v === "__none" ? "" : v })
                }
              >
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {walletsForSource.length > 0 && form.source !== "other" && (
              <div className="col-span-2 space-y-1.5">
                <Label>Wallet (optional)</Label>
                <Select
                  value={form.wallet_id || "__none"}
                  onValueChange={(v) =>
                    setForm({ ...form, wallet_id: v === "__none" ? "" : v })
                  }
                >
                  <SelectTrigger data-testid="expense-wallet-select">
                    <SelectValue placeholder="Any wallet of this source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Not linked —</SelectItem>
                    {walletsForSource.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2 space-y-1.5">
              <Label>Description *</Label>
              <Input
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="e.g. AWS monthly invoice"
                data-testid="expense-description-input"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional"
                data-testid="expense-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-expense-btn"
            >
              {editing ? "Save changes" : "Add expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage categories */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent data-testid="expense-categories-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Manage expense categories
            </DialogTitle>
            <DialogDescription>
              These appear in the form and the filter bar.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-1.5">
            <Input
              value={catInput}
              onChange={(e) => setCatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const v = catInput.trim();
                  if (!v) return;
                  if (catDraft.some((x) => x.toLowerCase() === v.toLowerCase()))
                    return toast.message(`"${v}" already exists`);
                  setCatDraft([...catDraft, v]);
                  setCatInput("");
                }
              }}
              placeholder="e.g. Subscription"
              className="h-9"
              data-testid="expense-category-input"
            />
            <Button
              onClick={() => {
                const v = catInput.trim();
                if (!v) return;
                if (catDraft.some((x) => x.toLowerCase() === v.toLowerCase()))
                  return toast.message(`"${v}" already exists`);
                setCatDraft([...catDraft, v]);
                setCatInput("");
              }}
              className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            >
              <Plus size={13} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {catDraft.map((c, i) => (
              <span
                key={`${c}-${i}`}
                className="inline-flex items-center gap-1 text-[12px] border hairline rounded px-1.5 py-0.5 bg-zinc-50"
              >
                {c}
                <button
                  onClick={() =>
                    setCatDraft(catDraft.filter((_, idx) => idx !== i))
                  }
                  className="text-zinc-400 hover:text-rose-600"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveCats}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-expense-categories-btn"
            >
              Save categories
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
